import React, { useState, useEffect } from 'react'
import { XMarkIcon, TrashIcon, FolderIcon } from '@heroicons/react/24/outline'

const LocalIconsModal = ({ isOpen, onClose, onSelectIcon }) => {
  const [localIcons, setLocalIcons] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deletingIcon, setDeletingIcon] = useState(null)

  // 获取本地图标列表
  const fetchLocalIcons = async () => {
    setIsLoading(true)
    try {
      const response = await fetch('/api/icons/local')
      const data = await response.json()
      setLocalIcons(data.icons || [])
    } catch (error) {
      console.error('获取本地图标失败:', error)
      setLocalIcons([])
    } finally {
      setIsLoading(false)
    }
  }

  // 删除本地图标
  const deleteIcon = async (fileName) => {
    if (!confirm(`确定要删除图标 "${fileName}" 吗？`)) {
      return
    }

    setIsDeleting(true)
    setDeletingIcon(fileName)

    try {
      const response = await fetch(`/api/icons/${fileName}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        // 重新获取图标列表
        await fetchLocalIcons()
      } else {
        throw new Error('删除图标失败')
      }
    } catch (error) {
      console.error('删除图标失败:', error)
      alert('删除图标失败，请重试')
    } finally {
      setIsDeleting(false)
      setDeletingIcon(null)
    }
  }

  // 选择图标
  const selectIcon = (icon) => {
    // 返回完整的 URL，让 Homepage 能够访问 Config Manager 的图标
    // 使用当前页面的 origin 来构建完整 URL
    const iconUrl = `${window.location.origin}/icons/${icon.fileName}`
    onSelectIcon(iconUrl)
    onClose()
  }

  // 组件挂载时获取图标列表
  useEffect(() => {
    if (isOpen) {
      fetchLocalIcons()
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* 标题栏 */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <FolderIcon className="h-6 w-6 text-primary-500" />
            <h2 className="text-xl font-semibold text-white">本地图标库</h2>
            <span className="text-sm text-gray-400">({localIcons.length} 个图标)</span>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* 内容区域 */}
        <div className="flex-1 overflow-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              <span className="ml-3 text-gray-400">加载中...</span>
            </div>
          ) : localIcons.length > 0 ? (
            <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 lg:grid-cols-12 gap-3">
              {localIcons.map((icon, index) => (
                <div
                  key={index}
                  className="group relative bg-gray-700 rounded-lg p-3 hover:bg-gray-600 transition-colors"
                >
                  {/* 图标预览 */}

                  <div className="aspect-square flex items-center justify-center mb-2 bg-white rounded border border-gray-300">
                    <img
                      src={`${window.location.origin}/icons/${icon.fileName}`}
                      alt={icon.name}
                      className="w-8 h-8 object-contain"
                      onError={(e) => {
                        console.error('图片加载失败:', `${window.location.origin}/icons/${icon.fileName}`, e)
                        // 尝试备用API路径
                        e.target.src = icon.url
                        e.target.onerror = (e2) => {
                          console.error('备用路径也失败:', icon.url, e2)
                          e2.target.style.display = 'none'
                          e2.target.nextSibling.style.display = 'block'
                        }
                      }}
                      onLoad={() => {
                        console.log('图片加载成功:', `${window.location.origin}/icons/${icon.fileName}`)
                      }}
                    />
                    <div className="w-8 h-8 bg-gray-600 rounded flex items-center justify-center text-gray-400 text-xs" style={{ display: 'none' }}>
                      ?
                    </div>
                  </div>

                  {/* 图标名称 */}
                  <p className="text-xs text-gray-300 text-center truncate" title={icon.name}>
                    {icon.name}
                  </p>

                  {/* 操作按钮 */}
                  <div className="absolute inset-0 bg-black bg-opacity-75 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex flex-col items-center justify-center gap-2">
                    <button
                      onClick={() => selectIcon(icon)}
                      className="btn-primary text-xs px-2 py-1"
                      title="使用此图标"
                    >
                      使用
                    </button>
                    <button
                      onClick={() => deleteIcon(icon.fileName)}
                      disabled={isDeleting}
                      className="btn-danger text-xs px-2 py-1 flex items-center gap-1"
                      title="删除图标"
                    >
                      {deletingIcon === icon.fileName ? (
                        <div className="animate-spin rounded-full h-3 w-3 border-b border-white"></div>
                      ) : (
                        <TrashIcon className="h-3 w-3" />
                      )}
                      删除
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <FolderIcon className="h-12 w-12 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400">暂无本地图标</p>
              <p className="text-sm text-gray-500 mt-2">
                使用图标搜索功能下载图标到本地
              </p>
            </div>
          )}
        </div>

        {/* 底部说明 */}
        <div className="p-6 border-t border-gray-700 bg-gray-750">
          <div className="text-sm text-gray-400">
            <p className="mb-2">
              <strong className="text-gray-300">本地图标库说明：</strong>
            </p>
            <ul className="list-disc list-inside space-y-1 text-xs">
              <li>本地图标存储在服务器的 <code className="bg-gray-600 px-1 rounded">icons/</code> 目录下</li>
              <li>使用本地图标可以提高加载速度，避免网络依赖</li>
              <li>支持多种图片格式：PNG、JPG、SVG、WebP、GIF、BMP、TIFF、ICO</li>
              <li>删除图标后无法恢复，请谨慎操作</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LocalIconsModal
